import { useEffect, useRef, useState } from "react";

/**
 * Sticky category navigation with scroll-spy — Uber-Eats / Deliveroo style.
 *
 * Props:
 *  - categories: array of { id: string, label: string } — `id` must match the
 *    `id` attribute on the corresponding section <div> in the menu.
 *  - stickyOffsetPx: number of pixels from the viewport top where the pill bar
 *    should "stick" (defaults to 0). Use this to clear any existing fixed
 *    header (e.g. the QR Menu / Counter top bars).
 *  - scrollOffsetPx: extra padding above the section when scrolling-to-anchor,
 *    so the section title isn't hidden under the sticky bar (defaults to 80).
 *
 * Behaviour:
 *  - Renders a horizontally-scrolling pill row that sticks to top when the
 *    user scrolls past it.
 *  - Tapping a pill smoothly scrolls to the matching `#<id>` section.
 *  - The pill matching the currently-visible section auto-highlights via
 *    IntersectionObserver. Best-match wins on multi-overlap.
 */
const CategoryNav = ({ categories = [], stickyOffsetPx = 0, scrollOffsetPx = 80 }) => {
    const [activeId, setActiveId] = useState(categories[0]?.id || null);
    const rowRef = useRef(null);

    // Scroll-spy: highlight the section currently most visible
    useEffect(() => {
        if (!categories.length) return undefined;
        // Build root margin so the "active" threshold is the top of the viewport
        // just below the sticky bar.
        const top = -(stickyOffsetPx + 40);
        const bottom = -(window.innerHeight - stickyOffsetPx - 120);
        const obs = new IntersectionObserver(
            (entries) => {
                // Of all currently intersecting sections, pick the one closest
                // to the top of the viewport (highest intersectionRect.top).
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length) setActiveId(visible[0].target.id);
            },
            { rootMargin: `${top}px 0px ${bottom}px 0px`, threshold: 0 },
        );
        for (const c of categories) {
            const el = document.getElementById(c.id);
            if (el) obs.observe(el);
        }
        return () => obs.disconnect();
    }, [categories, stickyOffsetPx]);

    // Keep the active pill scrolled into view inside the horizontal row
    useEffect(() => {
        if (!rowRef.current || !activeId) return;
        const pill = rowRef.current.querySelector(`[data-cat-pill="${activeId}"]`);
        if (pill?.scrollIntoView) {
            pill.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }
    }, [activeId]);

    const onJump = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - scrollOffsetPx;
        window.scrollTo({ top: y, behavior: "smooth" });
        // Optimistic highlight so the click feels instant
        setActiveId(id);
    };

    if (!categories.length) return null;

    return (
        <div
            className="sticky bg-[var(--bg)] border-b border-[var(--border)] z-20"
            // Sticky offset + iOS safe-area inset so the pill bar clears the
            // notch / status bar in PWA install mode.
            style={{ top: `calc(${stickyOffsetPx}px + env(safe-area-inset-top))` }}
            data-testid="category-nav"
        >
            <div
                ref={rowRef}
                className="flex gap-1.5 overflow-x-auto px-4 py-2.5 no-scrollbar"
                style={{ WebkitOverflowScrolling: "touch" }}
            >
                {categories.map((c) => {
                    const active = c.id === activeId;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            data-cat-pill={c.id}
                            data-testid={`category-pill-${c.id}`}
                            onClick={() => onJump(c.id)}
                            className={`whitespace-nowrap font-mono uppercase text-[10px] tracking-[0.18em] px-3.5 py-2 border transition-colors ${
                                active
                                    ? "bg-[var(--gold)] text-[#0a0a0a] border-[var(--gold)]"
                                    : "border-[var(--border)] text-muted hover:text-[var(--text)] hover:border-[var(--text)]"
                            }`}
                        >
                            {c.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryNav;
